/**
 * achievementsSlice — daily evaluation of milestone medals.
 *
 * State :
 *   unlockedAchievements: { [id]: { unlockedAt: dayNumber, viewed: bool } }
 *
 * Selectors :
 *   getAchievementProgress() → { unlockedCount, totalCount, byCategory }
 *   getUnviewedAchievements() → array of newly-unlocked achievements
 *
 * Actions :
 *   evaluateAchievements() → run every check, unlock new ones, push msg.
 *                            Idempotent — never re-locks anything.
 *   markAchievementViewed(id)
 *
 * Called from coreSlice.nextDay() at every day end.
 *
 * 29/05/2026 — Achievements feature.
 */
import { ACHIEVEMENTS, ACHIEVEMENT_BY_ID } from '../../data/achievements';

export const createAchievementsSlice = (set, get) => ({
  unlockedAchievements: {},

  evaluateAchievements: () => {
    const state = get();
    const unlocked = state.unlockedAchievements || {};
    const today = state.totalDays || 0;
    const newUnlocks = [];
    const updates = { ...unlocked };

    for (const ach of ACHIEVEMENTS) {
      if (updates[ach.id]) continue;  // already unlocked — skip
      let ok = false;
      try {
        ok = ach.check(state);
      } catch (err) {
        console.warn('[achievementsSlice] check failed:', ach.id, err);
        continue;
      }
      if (ok) {
        updates[ach.id] = { unlockedAt: today, viewed: false };
        newUnlocks.push(ach);
      }
    }

    if (newUnlocks.length === 0) return { unlocked: [] };

    set({ unlockedAchievements: updates });

    // Push a Messagerie message per unlock (or one bundled if multiple).
    if (typeof get().pushCustomMessage === 'function') {
      for (const ach of newUnlocks) {
        get().pushCustomMessage({
          senderType: 'support',
          senderName: 'HST — Carrière',
          senderRole: 'support',
          tone: 'positive',
          kind: 'info',
          subject: `🏆 Médaille débloquée — ${ach.name}`,
          // 07/02/2026 — P0 retour user prod : ancien body utilisait deux
          // segments `**...**` consécutifs (« **Notoriété installée** ! »
          // puis « **Description :** ») qui apparaissaient concaténés
          // dans tout viewer qui strippe le markdown (« Notoriété
          // installéeDescription : »). Body réécrit en français naturel
          // sans dépendre du parsing markdown.
          body:
            `Félicitations, vous venez de débloquer la médaille « ${ach.name} » !\n\n` +
            `${ach.description}\n\n` +
            `Rendez-vous dans la page Carrière pour consulter toutes vos médailles obtenues.`,
          ttlDays: 60,
          payload: { achievementId: ach.id },
        });
      }
    }

    return { unlocked: newUnlocks };
  },

  markAchievementViewed: (id) => {
    const state = get();
    const cur = state.unlockedAchievements || {};
    if (!cur[id] || cur[id].viewed) return;
    set({
      unlockedAchievements: {
        ...cur,
        [id]: { ...cur[id], viewed: true },
      },
    });
  },

  markAllAchievementsViewed: () => {
    const state = get();
    const cur = state.unlockedAchievements || {};
    const next = {};
    let changed = false;
    for (const id of Object.keys(cur)) {
      if (cur[id].viewed) next[id] = cur[id];
      else { next[id] = { ...cur[id], viewed: true }; changed = true; }
    }
    if (changed) set({ unlockedAchievements: next });
  },

  getAchievementProgress: () => {
    const state = get();
    const unlocked = state.unlockedAchievements || {};
    const unlockedCount = Object.keys(unlocked).length;
    const totalCount = ACHIEVEMENTS.length;
    const byCategory = {};
    for (const ach of ACHIEVEMENTS) {
      if (!byCategory[ach.category]) byCategory[ach.category] = { total: 0, unlocked: 0 };
      byCategory[ach.category].total += 1;
      if (unlocked[ach.id]) byCategory[ach.category].unlocked += 1;
    }
    return { unlockedCount, totalCount, byCategory };
  },

  getUnviewedAchievements: () => {
    const state = get();
    const unlocked = state.unlockedAchievements || {};
    return Object.keys(unlocked)
      .filter((id) => !unlocked[id].viewed)
      .map((id) => ACHIEVEMENT_BY_ID[id])
      .filter(Boolean);
  },
});
